import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { backend, API_URL } from "../../../config";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CssBaseline,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Button,
  CircularProgress,
  TextField,
  Modal,
  Link,
  Chip,
  Drawer,
  Autocomplete,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Stack,
  debounce,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  DragIndicator as DragIndicatorIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: 440,
    maxHeight: "75vh",
  },
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

const Level3Item = React.memo(({ item, index, toggleDrawer, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `level3-item-${item.id}`,
    data: { level: 3, itemId: item.id },
    animateLayoutChanges: defaultAnimateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging || !item.active ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        padding: "8px",
        margin: "4px",
        backgroundColor: isDragging ? "#fff59d" : "#f0f0f0",
        border: "1px solid #ccc",
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <IconButton
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          size="small"
          aria-label={`drag ${item.name}`}
          sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
        >
          <DragIndicatorIcon />
        </IconButton>
        <Typography variant="body1" component="div">
          <b>{item.name} </b>
        </Typography>
        {item.items_count > 0 && (
          <Chip
            label={`${item.items_count} items`}
            sx={{ opacity: item.items_count ? 1 : 0.5 }}
          />
        )}
        <div
          style={{
            display: "flex",
            flexGrow: 1,
            justifyContent: "flex-end",
          }}
        >
          <Link href={`/products/?category=${item.id}`} target="_blank">
            <Tooltip title={`View products in ${item.name}`}>
              <IconButton aria-label={`view products in ${item.name}`}>
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Link>
          <Tooltip title={`Edit ${item.name}`}>
            <IconButton
              aria-label={`edit ${item.name}`}
              onClick={(e) => toggleDrawer(e, item)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              (item.children && item.children.length > 0) ||
              item.items_count > 0
                ? `Cannot delete ${item.name} - it has children or items`
                : `Delete ${item.name}`
            }
          >
            <span>
              <IconButton
                aria-label={`delete ${item.name}`}
                disabled={
                  (item.children && item.children.length > 0) ||
                  item.items_count > 0
                }
                onClick={() => onDelete(item)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

const Level3Area = ({
  parentId,
  level3Item,
  toggleDrawer,
  onDelete,
  overId,
}) => {
  const items = level3Item.children.map((item) => `level3-item-${item.id}`);
  const areaId = `level3-area-${parentId}`;
  const { setNodeRef } = useDroppable({
    id: areaId,
  });
  const isOver =
    overId === areaId ||
    level3Item.children.some((child) => `level3-item-${child.id}` === overId);

  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        data-droppable-id={areaId}
        style={{
          padding: "8px",
          border: isOver ? "2px solid blue" : "1px dashed blue",
          backgroundColor: isOver ? "rgba(0, 0, 255, 0.05)" : "transparent",
          transition: "all 0.2s ease",
          minHeight: "40px",
        }}
      >
        {level3Item.children.map((item, index) => (
          <Level3Item
            key={`level3-item-${item.id}`}
            item={item}
            index={index}
            toggleDrawer={toggleDrawer}
            onDelete={onDelete}
          />
        ))}
      </div>
    </SortableContext>
  );
};

const Level2Item = React.memo(
  ({ item, index, toggleDrawer, onDelete, overId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: `level2-item-${item.id}`,
      data: { level: 2, itemId: item.id },
      animateLayoutChanges: defaultAnimateLayoutChanges,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging || !item.active ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={{
          padding: "8px",
          margin: "4px",
          backgroundColor: isDragging ? "#fff59d" : "#e0e0e0",
          border: "1px solid #999",
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            size="small"
            aria-label={`drag ${item.name}`}
            sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
          >
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="body1" component="div">
            <b>{item.name} </b>
          </Typography>
          {item.items_count > 0 && (
            <Chip
              label={`${item.items_count} items`}
              sx={{ opacity: item.items_count ? 1 : 0.5 }}
            />
          )}
          <div
            style={{
              display: "flex",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <Link href={`/products/?category=${item.id}`} target="_blank">
              <Tooltip title={`View products in ${item.name}`}>
                <IconButton aria-label={`view products in ${item.name}`}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </Link>
            <Tooltip title={`Add item to ${item.name}`}>
              <IconButton
                aria-label={`add item to ${item.name}`}
                onClick={(e) => toggleDrawer(e, item, true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Edit ${item.name}`}>
              <IconButton
                aria-label={`edit ${item.name}`}
                onClick={(e) => toggleDrawer(e, item)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                (item.children && item.children.length > 0) ||
                item.items_count > 0
                  ? `Cannot delete ${item.name} - it has children or items`
                  : `Delete ${item.name}`
              }
            >
              <span>
                <IconButton
                  aria-label={`delete ${item.name}`}
                  onClick={() => onDelete(item)}
                  disabled={
                    (item.children && item.children.length > 0) ||
                    item.items_count > 0
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <Level3Area
          parentId={item.id}
          level3Item={item}
          toggleDrawer={toggleDrawer}
          onDelete={onDelete}
          overId={overId}
        />
      </div>
    );
  },
);

const Level2Area = ({
  parentId,
  level2Item,
  toggleDrawer,
  onDelete,
  overId,
}) => {
  const items = level2Item.children.map((item) => `level2-item-${item.id}`);
  const areaId = `level2-area-${parentId}`;
  const { setNodeRef } = useDroppable({
    id: areaId,
  });
  const isOver =
    overId === areaId ||
    level2Item.children.some((child) => `level2-item-${child.id}` === overId);

  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        data-droppable-id={areaId}
        style={{
          padding: "8px",
          border: isOver ? "2px solid darkblue" : "1px dashed darkblue",
          backgroundColor: isOver ? "rgba(0, 0, 139, 0.05)" : "transparent",
          transition: "all 0.2s ease",
          minHeight: "40px",
        }}
      >
        {level2Item.children.map((item, index) => (
          <Level2Item
            key={`level2-item-${item.id}`}
            item={item}
            index={index}
            toggleDrawer={toggleDrawer}
            onDelete={onDelete}
            overId={overId}
          />
        ))}
      </div>
    </SortableContext>
  );
};

const Level1Area = ({ categories, toggleDrawer, onDelete, overId }) => {
  const areaId = "level1-area";
  const { setNodeRef } = useDroppable({
    id: areaId,
  });

  return (
    <div
      ref={setNodeRef}
      data-droppable-id={areaId}
      style={{
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        gap: "50px",
      }}
    >
      {categories.map((item, index) => {
        return (
          <Level1Item
            key={`level1-item-${item.id}`}
            item={item}
            index={index}
            toggleDrawer={toggleDrawer}
            onDelete={onDelete}
            overId={overId}
          />
        );
      })}
    </div>
  );
};

const Level1Item = React.memo(
  ({ item, index, toggleDrawer, onDelete, overId }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: `level1-item-${item.id}`,
      data: { level: 1, itemId: item.id },
      animateLayoutChanges: defaultAnimateLayoutChanges,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging || !item.active ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={{
          padding: "8px",
          backgroundColor: isDragging ? "#fff59d" : "#d0d0d0",
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            size="small"
            aria-label={`drag ${item.name}`}
            sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
          >
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="body1" component="div">
            <b>{item.name} </b>
          </Typography>
          {item.items_count > 0 && (
            <Chip
              label={`${item.items_count} items`}
              sx={{ opacity: item.items_count ? 1 : 0.5 }}
            />
          )}
          <div
            style={{
              display: "flex",
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
          >
            <Link href={`/products/?category=${item.id}`} target="_blank">
              <Tooltip title={`View products in ${item.name}`}>
                <IconButton aria-label={`view products in ${item.name}`}>
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </Link>
            <Tooltip title={`Add item to ${item.name}`}>
              <IconButton
                aria-label={`add item to ${item.name}`}
                onClick={(e) => toggleDrawer(e, item, true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Edit ${item.name}`}>
              <IconButton
                aria-label={`edit ${item.name}`}
                onClick={(e) => toggleDrawer(e, item)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                (item.children && item.children.length > 0) ||
                item.items_count > 0
                  ? `Cannot delete ${item.name} - it has children or items`
                  : `Delete ${item.name}`
              }
            >
              <span>
                <IconButton
                  aria-label={`delete ${item.name}`}
                  onClick={() => onDelete(item)}
                  disabled={
                    (item.children && item.children.length > 0) ||
                    item.items_count > 0
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <Level2Area
          parentId={item.id}
          level2Item={item}
          toggleDrawer={toggleDrawer}
          onDelete={onDelete}
          overId={overId}
        />
      </div>
    );
  },
);

const CategoryItemsRow = React.memo(({ data, index, style }) => {
  const item = data.items[index];

  if (!item) return null;

  return (
    <div style={style}>
      <Grid
        container
        spacing={0}
        alignItems="center"
        style={{ opacity: item.active ? 1 : 0.5 }}
      >
        <Grid item xs={"auto"}>
          <IconButton aria-label="drag">
            <DragIndicatorIcon />
          </IconButton>
        </Grid>
        <Grid item xs={true}>
          {item?.name}
        </Grid>
        <Grid item xs={"auto"}>
          <IconButton
            aria-label="delete"
            onClick={() =>
              data.handleChange({
                target: { name: "item_delete", value: item.upc },
              })
            }
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </div>
  );
});

const CategoryDrawerContent = React.memo(
  ({
    categoryData,
    flattenedCategories,
    handleChange,
    handleChangeProductInput,
    isCategoryLoading,
    isLoading,
    productOptions,
    selectedProduct,
    sendCategoryRequest,
    setSelectedProduct,
    toggleDrawer,
  }) => {
    const listData = useMemo(
      () => ({ items: categoryData?.items || [], handleChange }),
      [categoryData?.items, handleChange],
    );

    return (
      <div style={{ width: 400, padding: 16 }}>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item xs={12} align="right">
            <Tooltip title="Close">
              <IconButton onClick={toggleDrawer}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <Typography component="h4">
              <b>
                {categoryData?.name
                  ? `${categoryData?.id ? "Edit" : "Add"} Department: ${
                      categoryData.name
                    }`
                  : "Add New Department"}
              </b>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              required={true}
              id="outlined-basic"
              variant="outlined"
              label="Name"
              name="name"
              value={categoryData?.name || ""}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormControlLabel
              value="start"
              control={
                <Checkbox
                  name="active"
                  value={categoryData?.active || false}
                  checked={categoryData?.active || false}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="Active"
              labelPlacement="end"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <InputLabel shrink id="categoryDepthLabel">
              Department Parent
            </InputLabel>
            <Autocomplete
              size="small"
              name="category_depth"
              options={flattenedCategories}
              getOptionLabel={(option) => option.name || ""}
              getOptionDisabled={(option) =>
                !option.active || option.id === categoryData?.id
              }
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    paddingLeft: `${16 + option.depth * 20}px`,
                    color:
                      option.depth === 0
                        ? "#000"
                        : option.depth === 1
                          ? "#333"
                          : "#666",
                    fontSize: `${14 - option.depth * 1}px`,
                    fontWeight:
                      option.depth === 0 ? 600 : option.depth === 1 ? 500 : 400,
                  }}
                >
                  <span
                    style={{
                      marginRight: 8,
                      opacity: 0.6,
                      fontWeight: 400,
                    }}
                  >
                    {option.depth === 1 ? "→ " : option.depth === 2 ? "↳ " : ""}
                  </span>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "rgba(255, 255, 255, 0.95)",
                      background: "rgba(255, 255, 255, 0.12)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.25)",
                      borderRadius: "4px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.18)",
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                      },
                      "&.Mui-focused": {
                        background: "rgba(255, 255, 255, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.6)",
                        boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                      },
                    },
                    "& .MuiOutlinedInput-input::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                      opacity: 1,
                    },
                  }}
                />
              )}
              value={categoryData?.parent || null}
              onChange={(e, v) =>
                handleChange({ target: { name: "category_depth", value: v } })
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid item xs={true}>
            <InputLabel shrink id="productLabel">
              Product
            </InputLabel>
            <Autocomplete
              fullWidth
              disablePortal
              size="small"
              name="product_id"
              options={productOptions}
              getOptionLabel={(option) =>
                `${option.name} | (${option.upc}) #${
                  option.item_code.item_code || ""
                }#`
              }
              isOptionEqualToValue={(option, value) => option.upc === value.upc}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField {...params} size="small" variant="standard" />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  {`${option.name} | (${option.upc}) #${
                    option.item_code.item_code || ""
                  }# Stock: ${option.stock_level || 0} `}
                  {["Unit", "Unit / Case"].includes(option.unit_type)
                    ? `Unit: ${option.display_unit_price} `
                    : ""}
                  {["Case", "Unit / Case"].includes(option.unit_type)
                    ? `Case: ${option.display_case_price}`
                    : ""}
                </li>
              )}
              getOptionDisabled={(option) => {
                return Boolean(categoryData?.items?.includes(option.upc));
              }}
              loading={isLoading}
              onChange={(e, value) => setSelectedProduct(value)}
              onInputChange={handleChangeProductInput}
            />
          </Grid>
          <Grid item xs={"auto"}>
            <Button
              disabled={selectedProduct === null}
              variant="contained"
              onClick={() => {
                handleChange({
                  target: {
                    name: "items",
                    value: selectedProduct,
                  },
                });
              }}
            >
              Add
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography component="h4">
              <b>List of products</b>
            </Typography>
            <br />
            <div style={{ maxHeight: "50vh", overflow: "auto" }}>
              {isCategoryLoading ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={22} />
                </div>
              ) : categoryData.items && categoryData.items.length > 0 ? (
                <div style={{ height: "50vh" }}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <List
                        height={height}
                        width={width}
                        itemCount={categoryData.items.length}
                        itemSize={48}
                        itemData={listData}
                        overscanCount={8}
                      >
                        {CategoryItemsRow}
                      </List>
                    )}
                  </AutoSizer>
                </div>
              ) : (
                <>There are no products assigned</>
              )}
            </div>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} justifyContent="right">
              <Button onClick={toggleDrawer}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => sendCategoryRequest()}
                disabled={isCategoryLoading}
              >
                Save
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </div>
    );
  },
);

const Categories = (props) => {
  const classes = useStyles();

  const categoryInitialState = {
    id: null,
    name: "",
    parent: null,
    children: [],
    items: [],
    active: true,
  };

  const [categories, setCategories] = useState([]);
  const [flattenedCategories, setFlattenedCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [categoryModal, setCategoryModal] = useState(false);
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [categoryData, setCategoryData] = useState(categoryInitialState);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);

  const handleChange = (e) => {
    const { checked, name, value } = e.target;

    setCategoryData((prevState) => {
      let categoryObj = { ...prevState };

      if (["active"].includes(name)) {
        categoryObj = { ...categoryObj, [name]: checked };
      } else if (name === "item_delete") {
        categoryObj = {
          ...categoryObj,
          items: categoryObj.items.filter((item) => item.upc !== value),
        };
      } else if (name === "items") {
        categoryObj = {
          ...categoryObj,
          items: [...categoryObj.items, { upc: value.upc, name: value.name }],
        };
      } else if (name === "category_depth") {
        let getParentObj = null;

        if (value !== null) {
          categories.forEach((cat) => {
            if (cat.id === value.id) {
              getParentObj = cat;
            } else {
              cat.children.forEach((child) => {
                if (child.id === value.id) {
                  getParentObj = child;
                }
              });
            }
          });
        }

        categoryObj = {
          ...categoryObj,
          parent: getParentObj,
        };
      } else {
        categoryObj = { ...categoryObj, [name]: value };
      }

      return categoryObj;
    });
  };

  const handleCloseCategoryModal = () => {
    toggleCategoryModal();
    resolveError();
  };

  const resolveError = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    setErrorInfo({ error: false, message: "" });
  };

  const toggleCategoryModal = () => setCategoryModal(!categoryModal);

  const categoryModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <Grid container spacing={2}>
          <Grid item xs={12}></Grid>
          <Grid item xs={12}>
            Are you sure you want to delete the <b>{categoryData.name}</b>{" "}
            category?
          </Grid>
          <Grid item xs={12} sm={12}>
            <div align="right">
              <Button
                onClick={handleCloseCategoryModal}
                className={classes.button}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => sendCategoryDeleteRequest()}
                className={classes.button}
                disabled={
                  !categoryData ||
                  (categoryData.children && categoryData.children.length > 0) ||
                  (categoryData.items && categoryData.items.length > 0)
                }
              >
                Confirm
              </Button>
            </div>
          </Grid>
        </Grid>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>Deleting category</div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while deleting the category
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {typeof errorInfo.message === "object" ? (
                  <>
                    <ul>
                      {Object.keys(errorInfo.message).map((key) => (
                        <li key={key}>
                          <b>{key}:</b>
                          {` ${errorInfo.message[key]}`}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  errorInfo.message
                )}
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleCloseCategoryModal()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The category has been deleted successfully
          <br />
          <br />
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleCloseCategoryModal()}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const sendCategoryRequest = async () => {
    setLoadingFlag(true);
    const objPayload = {
      name: categoryData.name,
      active: categoryData.active,
      parent_id: categoryData.parent ? categoryData.parent.id : null,
      children_ids: categoryData.children
        ? categoryData.children.map((child) => child.id)
        : [],
      item_ids: categoryData.items
        ? categoryData.items.map((item) => item.upc)
        : [],
    };

    if (categoryData?.id) {
      await backend
        .patch(`${API_URL}productcategories/${categoryData.id}`, objPayload)
        .then(({ data }) => {
          setLoadingFlag(false);
          setFinishedFlag(true);

          getCategories();
          toggleDrawer();
        })
        .catch((error) => {
          console.log(error);
          const errorData = error.response.data;

          setErrorInfo({
            error: true,
            message:
              typeof errorData === "object"
                ? errorData.hasOwnProperty("message")
                  ? errorData.message
                  : errorData.hasOwnProperty("message")
                    ? errorData.detail
                    : errorData
                : error.message,
          });
        });
    } else {
      await backend
        .post(`${API_URL}productcategories/`, objPayload)
        .then(({ data }) => {
          setLoadingFlag(false);
          setFinishedFlag(true);

          getCategories();
          toggleDrawer();
        })
        .catch((error) => {
          console.log(error);
          const errorData = error.response.data;

          setErrorInfo({
            error: true,
            message:
              typeof errorData === "object"
                ? errorData.hasOwnProperty("message")
                  ? errorData.message
                  : errorData.hasOwnProperty("message")
                    ? errorData.detail
                    : errorData
                : error.message,
          });
        });
    }
  };

  const sendCategoryDeleteRequest = async () => {
    setLoadingFlag(true);

    const categoryId = categoryData.id || undefined;
    await backend
      .delete(`${API_URL}productcategories/${categoryId}`)
      .then(({ data }) => {
        setLoadingFlag(false);
        setFinishedFlag(true);
        getCategories();
        handleCloseCategoryModal();
      })
      .catch((error) => {
        console.log(error);
        setErrorInfo({
          error: true,
          message:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message,
        });
      });
  };

  const sendCategoryOrderRequest = async (categoriesToUpdate) => {
    // Collect all items with their display_order values
    // Each level maintains independent ordering within its parent
    const itemsToUpdate = [];

    // Process Level 1 items
    categoriesToUpdate.forEach((item, index) => {
      itemsToUpdate.push({
        id: item.id,
        display_order: index,
        parent_id: null,
      });

      // Process Level 2 items (children of Level 1)
      if (item.children && item.children.length > 0) {
        item.children.forEach((child, childIndex) => {
          itemsToUpdate.push({
            id: child.id,
            display_order: childIndex,
            parent_id: item.id,
          });

          // Process Level 3 items (children of Level 2)
          if (child.children && child.children.length > 0) {
            child.children.forEach((grandchild, grandchildIndex) => {
              itemsToUpdate.push({
                id: grandchild.id,
                display_order: grandchildIndex,
                parent_id: child.id,
              });
            });
          }
        });
      }
    });

    setLoadingFlag(true);

    try {
      const categoriesPayload = { categories: itemsToUpdate };
      await backend.patch(
        `${API_URL}productcategoriesreorder/`,
        categoriesPayload,
      );

      setFinishedFlag(true);
      setLoadingFlag(false);
    } catch (error) {
      console.log("Error sending category order:", error);
      setErrorInfo({
        error: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.message,
      });
      setLoadingFlag(false);
    }
  };

  const flattenCategories = useCallback(
    (categories, depth = 0, parentPath = "") => {
      let flattened = [];

      categories
        .filter((category) => category.active)
        .forEach((category) => {
          const fullPath = parentPath
            ? `${parentPath} > ${category.name}`
            : category.name;

          flattened.push({
            id: category.id,
            name: category.name,
            depth,
            fullPath,
            parent_id: category.parent?.id || null,
            active: category.active,
          });

          if (category.children && category.children.length > 0 && depth < 1) {
            flattened = [
              ...flattened,
              ...flattenCategories(category.children, depth + 1, fullPath),
            ];
          }
        });

      return flattened;
    },
    [],
  );

  const getCategories = useCallback(async () => {
    await backend
      .get(`${API_URL}productcategories?no_page=1`)
      .then(({ data }) => {
        const dataMap = data.map((item) => ({
          id: item.id,
          name: item.name,
          children: item.children,
          items_count: item.items_count,
          display_order: item.display_order,
          active: item.active,
        }));

        setCategories(dataMap);
        const flattened = flattenCategories(data);
        setFlattenedCategories(flattened);
        setCategoriesLoaded(true);
      })
      .catch((error) => {
        console.log("Error fetching categories:", error);
        setCategoriesLoaded(true);
      });
  }, [flattenCategories]);

  const onDelete = (category) => {
    setCategoryData(category);
    setLoadingFlag(false);
    setFinishedFlag(false);
    setErrorInfo({ error: false, message: "" });
    setCategoryModal(true);
  };

  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // ONLY track which zone/item is being hovered over for visual feedback
    // DO NOT move items here - that happens in handleDragEnd
    setOverId(over.id);
  }, []);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event) => {
    setActiveId(null);
    setOverId(null);
    const { active, over } = event;

    if (!over) {
      console.log("Dropped outside any droppable area");
      return;
    }

    // Calculate the maximum depth of children for an item
    const calculateChildrenDepth = (item) => {
      if (!item.children || item.children.length === 0) {
        return 0;
      }
      const childDepths = item.children.map((child) =>
        calculateChildrenDepth(child),
      );
      return 1 + Math.max(...childDepths);
    };

    // Check if an item is a descendant of another item
    const isDescendant = (parentItem, childId) => {
      if (parentItem.id === childId) {
        return true;
      }
      if (!parentItem.children || parentItem.children.length === 0) {
        return false;
      }
      return parentItem.children.some((child) => isDescendant(child, childId));
    };

    // Extract the item ID and level from the drag ID
    const parseActiveId = (activeId) => {
      if (activeId.includes("level1-item-")) {
        return { level: 1, id: parseInt(activeId.replace("level1-item-", "")) };
      }
      if (activeId.includes("level2-item-")) {
        return { level: 2, id: parseInt(activeId.replace("level2-item-", "")) };
      }
      if (activeId.includes("level3-item-")) {
        return { level: 3, id: parseInt(activeId.replace("level3-item-", "")) };
      }
      return null;
    };

    // Find and remove the dragged item from anywhere in the hierarchy
    const findAndRemoveItem = (items, activeInfo) => {
      let draggedItem = null;

      if (!activeInfo) return { items, draggedItem };

      for (let i = 0; i < items.length; i++) {
        // Check if this is the item we're looking for at Level 1
        if (activeInfo.level === 1 && items[i].id === activeInfo.id) {
          draggedItem = items[i];
          return {
            items: [...items.slice(0, i), ...items.slice(i + 1)],
            draggedItem,
          };
        }

        // Search in children (Level 2 items)
        if (items[i].children && items[i].children.length > 0) {
          if (activeInfo.level === 2) {
            // Look for Level 2 item in children
            const childIndex = items[i].children.findIndex(
              (child) => child.id === activeInfo.id,
            );
            if (childIndex !== -1) {
              draggedItem = items[i].children[childIndex];
              const newChildren = [
                ...items[i].children.slice(0, childIndex),
                ...items[i].children.slice(childIndex + 1),
              ];
              return {
                items: items.map((item, idx) =>
                  idx === i ? { ...item, children: newChildren } : item,
                ),
                draggedItem,
              };
            }
          }

          // Search deeper in Level 3 items
          if (activeInfo.level === 3) {
            let foundInLevel2 = -1;
            let foundInLevel3 = -1;

            for (let j = 0; j < items[i].children.length; j++) {
              if (items[i].children[j].children) {
                const level3Index = items[i].children[j].children.findIndex(
                  (child) => child.id === activeInfo.id,
                );
                if (level3Index !== -1) {
                  draggedItem = items[i].children[j].children[level3Index];
                  foundInLevel2 = j;
                  foundInLevel3 = level3Index;
                  break;
                }
              }
            }

            if (draggedItem) {
              return {
                items: items.map((item, idx) => {
                  if (idx === i) {
                    return {
                      ...item,
                      children: item.children.map((level2Item, l2Idx) => {
                        if (l2Idx === foundInLevel2) {
                          return {
                            ...level2Item,
                            children: [
                              ...level2Item.children.slice(0, foundInLevel3),
                              ...level2Item.children.slice(foundInLevel3 + 1),
                            ],
                          };
                        }
                        return level2Item;
                      }),
                    };
                  }
                  return item;
                }),
                draggedItem,
              };
            }
          }

          // Recursively search deeper if not found yet
          const result = findAndRemoveItem(items[i].children, activeInfo);
          if (result.draggedItem) {
            return {
              items: items.map((item, idx) =>
                idx === i ? { ...item, children: result.items } : item,
              ),
              draggedItem: result.draggedItem,
            };
          }
        }
      }
      return { items, draggedItem };
    };

    // Helper function to find the parent of a given item and its level
    const findParentInfo = (items, targetId, currentLevel = 1) => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].id === targetId) {
          return { parent: items[i], level: currentLevel };
        }

        if (items[i].children && items[i].children.length > 0) {
          // Search in Level 2 items
          if (currentLevel === 1) {
            for (let j = 0; j < items[i].children.length; j++) {
              if (items[i].children[j].id === targetId) {
                return {
                  parent: items[i].children[j],
                  level: 2,
                  grandParent: items[i],
                };
              }

              // Search in Level 3 items
              if (
                items[i].children[j].children &&
                items[i].children[j].children.length > 0
              ) {
                for (let k = 0; k < items[i].children[j].children.length; k++) {
                  if (items[i].children[j].children[k].id === targetId) {
                    return {
                      parent: items[i].children[j].children[k],
                      level: 3,
                      grandParent: items[i].children[j],
                      greatGrandParent: items[i],
                    };
                  }
                }
              }
            }
          }
        }
      }
      return null;
    };

    // Determine drop zone type and target
    const getDropZoneInfo = (overId, activeInfo, categories) => {
      if (overId.includes("level1-area")) {
        // Dropping into the Level 1 area (gaps between Level 1 items) → becomes Level 1
        return { targetLevel: 1, parentId: null };
      }
      if (overId.includes("level3-area-")) {
        // Dropping into a Level 3 area → becomes Level 3
        const parentId = parseInt(overId.replace("level3-area-", ""));
        return { targetLevel: 3, parentId };
      }
      if (overId.includes("level2-area-")) {
        // Dropping into a Level 2 area → becomes Level 2
        const parentId = parseInt(overId.replace("level2-area-", ""));
        return { targetLevel: 2, parentId };
      }
      if (overId.includes("level3-item-")) {
        // Dropping on a Level 3 item → becomes Level 3 sibling
        const targetId = parseInt(overId.replace("level3-item-", ""));
        const parentInfo = findParentInfo(categories, targetId);

        if (parentInfo && parentInfo.level === 3 && parentInfo.grandParent) {
          // The parent of the Level 3 item is a Level 2 item
          return {
            targetLevel: 3,
            itemId: targetId,
            reorder: true,
            parentId: parentInfo.grandParent.id,
          };
        }
        return null;
      }
      if (overId.includes("level2-item-")) {
        // Dropping on a Level 2 item
        const targetId = parseInt(overId.replace("level2-item-", ""));

        // If dragging a Level 2 item onto another Level 2 item → reorder at Level 2
        if (activeInfo && activeInfo.level === 2) {
          return { targetLevel: 2, itemId: targetId, reorder: true };
        }

        // Otherwise → becomes Level 3 child of that Level 2 item
        return { targetLevel: 3, parentId: targetId };
      }
      if (overId.includes("level1-item-")) {
        // Dropping on a Level 1 item
        const targetId = parseInt(overId.replace("level1-item-", ""));

        // If dragging a Level 1 item onto another Level 1 item → reorder at Level 1
        if (activeInfo && activeInfo.level === 1) {
          return { targetLevel: 1, itemId: targetId, reorder: true };
        }

        // Otherwise → becomes Level 2 child of that Level 1 item
        return { targetLevel: 2, parentId: targetId };
      }
      return null;
    };

    // Insert item at the correct level based on drop zone
    const insertItemAtZone = (items, zoneInfo, itemToInsert) => {
      if (!zoneInfo || !itemToInsert) return items;

      if (
        zoneInfo.targetLevel === 1 &&
        !zoneInfo.reorder &&
        zoneInfo.parentId === null
      ) {
        // Inserting into Level 1 area (gaps between items) → append at end
        return [...items, itemToInsert];
      }

      if (zoneInfo.targetLevel === 1 && zoneInfo.reorder) {
        // Reordering at Level 1
        const overIndex = items.findIndex(
          (item) => item.id === zoneInfo.itemId,
        );
        if (overIndex !== -1) {
          // Keep all children as-is when reordering Level 1
          const newItems = [...items];
          newItems.splice(overIndex, 0, itemToInsert);
          return newItems;
        }
        // If not found, append to end
        return [...items, itemToInsert];
      }

      if (zoneInfo.targetLevel === 2 && zoneInfo.reorder) {
        // Reordering at Level 2
        return items.map((level1Item) => {
          if (level1Item.children) {
            const overIndex = level1Item.children.findIndex(
              (item) => item.id === zoneInfo.itemId,
            );
            if (overIndex !== -1) {
              // Keep children as-is when reordering Level 2
              const newChildren = [...level1Item.children];
              newChildren.splice(overIndex, 0, itemToInsert);
              return { ...level1Item, children: newChildren };
            }
          }
          return level1Item;
        });
      }

      if (zoneInfo.targetLevel === 3 && zoneInfo.reorder) {
        // Reordering at Level 3 (inserting as sibling of a Level 3 item)
        return items.map((level1Item) => {
          if (level1Item.children) {
            return {
              ...level1Item,
              children: level1Item.children.map((level2Item) => {
                if (
                  level2Item.id === zoneInfo.parentId &&
                  level2Item.children
                ) {
                  const overIndex = level2Item.children.findIndex(
                    (item) => item.id === zoneInfo.itemId,
                  );
                  if (overIndex !== -1) {
                    // Insert item as-is (depth validation prevents exceeding max level)
                    const newLevel3Items = [...level2Item.children];
                    newLevel3Items.splice(overIndex, 0, itemToInsert);
                    return { ...level2Item, children: newLevel3Items };
                  }
                }
                return level2Item;
              }),
            };
          }
          return level1Item;
        });
      }

      if (zoneInfo.targetLevel === 2 && !zoneInfo.reorder) {
        // Inserting as Level 2 (child of a Level 1 item)
        return items.map((item) => {
          if (item.id === zoneInfo.parentId) {
            // Shift children down: current children become grandchildren (Level 3)
            const shiftedItem = {
              ...itemToInsert,
              children: itemToInsert.children || [],
            };
            return {
              ...item,
              children: [...(item.children || []), shiftedItem],
            };
          }
          return item;
        });
      }

      if (zoneInfo.targetLevel === 3) {
        // Inserting as Level 3 (child of a Level 2 item)
        return items.map((level1Item) => {
          if (level1Item.children) {
            return {
              ...level1Item,
              children: level1Item.children.map((level2Item) => {
                if (level2Item.id === zoneInfo.parentId) {
                  // Insert item as-is (depth validation prevents exceeding max level)
                  return {
                    ...level2Item,
                    children: [...(level2Item.children || []), itemToInsert],
                  };
                }
                return level2Item;
              }),
            };
          }
          return level1Item;
        });
      }

      return items;
    };

    setCategories((prevCategories) => {
      // Parse the active item info
      const activeInfo = parseActiveId(active.id);

      // Remove the dragged item from wherever it is
      const { items: categoriesWithoutDragged, draggedItem } =
        findAndRemoveItem(prevCategories, activeInfo);

      // If we found the item, validate and insert it at the appropriate zone
      if (draggedItem) {
        const zoneInfo = getDropZoneInfo(over.id, activeInfo, prevCategories);
        if (zoneInfo) {
          // Prevent dropping an item onto itself
          if (
            zoneInfo.itemId === draggedItem.id ||
            zoneInfo.parentId === draggedItem.id
          ) {
            console.warn("Cannot drop an item onto itself");
            return prevCategories;
          }

          // Prevent dropping an item into itself or its descendants
          if (
            zoneInfo.parentId &&
            isDescendant(draggedItem, zoneInfo.parentId)
          ) {
            console.warn("Cannot drop an item into itself or its descendants");
            return prevCategories;
          }

          // Calculate the depth of children for the dragged item
          const childrenDepth = calculateChildrenDepth(draggedItem);

          // Validate: targetLevel + childrenDepth must be <= 3
          if (zoneInfo.targetLevel + childrenDepth > 3) {
            console.warn(
              `Cannot move item: would exceed level 3 (target: ${zoneInfo.targetLevel}, children depth: ${childrenDepth})`,
            );
            // Return original state - item stays where it was
            return prevCategories;
          }

          const newCategories = insertItemAtZone(
            categoriesWithoutDragged,
            zoneInfo,
            draggedItem,
          );

          // Send the updated display_order to backend
          sendCategoryOrderRequest(newCategories);

          return newCategories;
        } else {
          // If we couldn't determine drop zone, put the item back where it was
          return prevCategories;
        }
      }

      return prevCategories;
    });

    console.log("Drag End - Item moved from", active.id, "to", over.id);
  }, []);

  const getDraggedItem = useCallback(() => {
    if (!activeId) return null;

    const findItem = (items) => {
      for (let item of items) {
        if (`level${item.level || 1}-item-${item.id}` === activeId) {
          return item;
        }
        if (item.children) {
          const found = findItem(
            item.children.map((child) => ({
              ...child,
              level: (item.level || 1) + 1,
            })),
          );
          if (found) return found;
        }
      }
      return null;
    };

    return findItem(categories.map((cat) => ({ ...cat, level: 1 })));
  }, [activeId, categories]);

  const draggedItem = getDraggedItem();

  const handleChangeProductInput = debounce((e) => {
    const { value } = e.target;
    setProductOptions([]);
    setIsLoading(true);
    setProductInputValue(value);
  }, 300);

  const normalizeCategoryItems = useCallback((items) => {
    if (!Array.isArray(items)) return [];

    return items
      .map((item) => ({
        upc:
          item.upc ??
          item.item_upc ??
          item.item?.upc ??
          item.item_id ??
          item.id,
        name:
          item.name ??
          item.item_name ??
          item.item?.name ??
          item.description ??
          "",
        active: item.active ?? false,
      }))
      .filter((item) => item.upc != null);
  }, []);

  const resolveParentOption = useCallback(
    (parentId) => {
      if (!parentId) return null;
      return (
        flattenedCategories.find((option) => option.id === parentId) || null
      );
    },
    [flattenedCategories],
  );

  const fetchCategoryDetails = useCallback(
    async (category) => {
      if (!category?.id) return;

      try {
        setIsCategoryLoading(true);
        const { data } = await backend.get(
          `${API_URL}productcategories/${category.id}`,
        );
        const parentId =
          data?.parent?.id ??
          data?.parent_id ??
          category.parent?.id ??
          category.parent_id;

        setCategoryData((prev) => ({
          ...prev,
          ...category,
          ...data,
          parent: resolveParentOption(parentId),
          children: data?.children || category.children || [],
          items: normalizeCategoryItems(
            data?.items || data?.products || data?.product_items || [],
          ),
        }));
        setIsCategoryLoading(false);
      } catch (error) {
        console.log("Error fetching category details:", error);
        setCategoryData((prev) => ({
          ...prev,
          ...category,
        }));
        setIsCategoryLoading(false);
      }
    },
    [normalizeCategoryItems, resolveParentOption],
  );

  const toggleDrawer = (e, category = null, addChildren = false) => {
    if (category !== null) {
      if (addChildren) {
        category = { ...categoryInitialState, parent: category };
      }

      setCategoryData({
        ...categoryInitialState,
        ...category,
        items: category.items || [],
        children: category.children || [],
      });
      setSelectedProduct(null);
      setProductInputValue("");
      setProductOptions([]);
      setIsCategoryLoading(false);

      if (!addChildren && category?.id) {
        fetchCategoryDetails(category);
      }
    } else {
      setCategoryData(categoryInitialState);
      setIsCategoryLoading(false);
    }

    setDrawerStatus(!drawerStatus);
  };

  useEffect(() => {
    const getProductOptions = async () => {
      await backend
        .get(
          `${API_URL}products?no_page=1&warehouse=2&search=${productInputValue}`,
        )
        .then(({ data }) => {
          setProductOptions(data);
          setIsLoading(false);
        });
    };

    if (productInputValue?.length >= 3) {
      getProductOptions();
    }
  }, [productInputValue]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      distance: 8,
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
  );

  const level1Items = useMemo(
    () => categories.map((item) => `level1-item-${item.id}`),
    [categories],
  );

  return (
    <Fragment>
      <CssBaseline />
      <Modal open={categoryModal} onClose={handleCloseCategoryModal}>
        {categoryModalContent}
      </Modal>
      <Drawer
        anchor="right"
        open={drawerStatus}
        ModalProps={{ keepMounted: true }}
      >
        <CategoryDrawerContent
          categoryData={categoryData}
          flattenedCategories={flattenedCategories}
          handleChange={handleChange}
          handleChangeProductInput={handleChangeProductInput}
          isCategoryLoading={isCategoryLoading}
          isLoading={isLoading}
          productOptions={productOptions}
          selectedProduct={selectedProduct}
          sendCategoryRequest={sendCategoryRequest}
          setSelectedProduct={setSelectedProduct}
          toggleDrawer={toggleDrawer}
        />
      </Drawer>
      <Grid container spacing={2}>
        <Grid item xs={true} sm={true}>
          <Typography variant="h6" component="div">
            Departments
          </Typography>
        </Grid>
        <Grid item xs={"auto"}>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => toggleDrawer(e)}
          >
            <Typography variant="subtitle2" className="text-white">
              Add Department
            </Typography>
          </Button>
        </Grid>
        <Grid item xs={12} />
        {!categoriesLoaded && categories.length === 0 && (
          <Grid item xs={12} align="center">
            {!categoriesLoaded && <CircularProgress />}
            <Typography variant="h6">
              {!categoriesLoaded
                ? "Loading categories..."
                : "No categories found."}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={level1Items}
              strategy={verticalListSortingStrategy}
            >
              <Level1Area
                categories={categories}
                toggleDrawer={toggleDrawer}
                onDelete={onDelete}
                overId={overId}
              />
            </SortableContext>
            <DragOverlay>
              {draggedItem ? (
                <div
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "#fff59d",
                    border: "2px solid #fbc02d",
                    borderRadius: "4px",
                    boxShadow: "0 5px 12px rgba(0, 0, 0, 0.15)",
                    opacity: 0.95,
                    minWidth: "150px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "grabbing",
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: "16px" }} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <span>
                      <b>{draggedItem.name} </b>
                      {draggedItem.children &&
                        draggedItem.children.length > 0 && (
                          <>
                            - {draggedItem.children.length} child
                            {draggedItem.children.length !== 1 ? "ren" : ""}
                          </>
                        )}
                    </span>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default Categories;
